import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, type ApplicationSummary, type FeedbackRow, type Summary, api } from '../api.js';
import { relativeTime, slaState, statusLabel, typeLabel } from '../format.js';

const OPEN_STATUSES = ['new', 'triaged', 'accepted', 'in_progress', 'in_review', 'needs_info'];

/**
 * The cross-system inbox.
 *
 * The default view is deliberately opinionated: open work, newest first,
 * duplicates folded away. What makes this system worth having is being able to
 * see every application at once, so the application filter starts empty rather
 * than pre-selected.
 */
export function Inbox() {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState<FeedbackRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [applications, setApplications] = useState<ApplicationSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filters = useMemo(
    () => ({
      appId: params
        .getAll('appId')
        .flatMap((v) => v.split(','))
        .filter(Boolean),
      status: params.get('status')
        ? params.get('status')!.split(',').filter(Boolean)
        : OPEN_STATUSES,
      severity: params
        .getAll('severity')
        .flatMap((v) => v.split(','))
        .filter(Boolean),
      search: params.get('search') ?? '',
      slaBreached: params.get('slaBreached') === 'true',
      sort: params.get('sort') ?? 'created_desc',
    }),
    [params],
  );

  const setFilter = useCallback(
    (key: string, value: string | undefined) => {
      const next = new URLSearchParams(params);
      if (!value) next.delete(key);
      else next.set(key, value);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([api.list(filters), api.summary()])
      .then(([page, counts]) => {
        if (cancelled) return;
        setRows(page.items);
        setCursor(page.nextCursor);
        setSummary(counts);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'could not load the inbox');
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [filters]);

  useEffect(() => {
    api
      .applications()
      .then(setApplications)
      .catch(() => setApplications([]));
  }, []);

  const loadMore = async () => {
    if (!cursor) return;
    const page = await api.list({ ...filters, cursor });
    setRows((current) => [...current, ...page.items]);
    setCursor(page.nextCursor);
  };

  const openCount = OPEN_STATUSES.reduce(
    (total, status) => total + (summary?.byStatus[status] ?? 0),
    0,
  );

  return (
    <>
      <div className="stats">
        <div className="stat">
          <div className="stat-value">{openCount}</div>
          <div className="stat-label">Open reports</div>
        </div>
        <div className="stat">
          <div className="stat-value">{summary?.byStatus.new ?? 0}</div>
          <div className="stat-label">Awaiting triage</div>
        </div>
        <div className="stat">
          <div className="stat-value">{summary?.unassigned ?? 0}</div>
          <div className="stat-label">Unassigned</div>
        </div>
        <div className={`stat${(summary?.slaBreached ?? 0) > 0 ? ' alert' : ''}`}>
          <div className="stat-value">{summary?.slaBreached ?? 0}</div>
          <div className="stat-label">Past SLA</div>
        </div>
        <div className={`stat${(summary?.bySeverity.critical ?? 0) > 0 ? ' alert' : ''}`}>
          <div className="stat-value">{summary?.bySeverity.critical ?? 0}</div>
          <div className="stat-label">Critical open</div>
        </div>
      </div>

      <div className="filters">
        <input
          type="search"
          placeholder="Search titles, descriptions, or UF-1042…"
          defaultValue={filters.search}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              setFilter('search', (event.target as HTMLInputElement).value || undefined);
            }
          }}
        />

        <select
          value={filters.appId[0] ?? ''}
          onChange={(event) => setFilter('appId', event.target.value || undefined)}
        >
          <option value="">All systems</option>
          {applications.map((application) => (
            <option key={application.id} value={application.id}>
              {application.displayName}
            </option>
          ))}
        </select>

        <select
          value={params.get('status') ?? ''}
          onChange={(event) => setFilter('status', event.target.value || undefined)}
        >
          <option value="">Open</option>
          <option value="new">Awaiting triage</option>
          <option value="accepted,in_progress,in_review">Being worked on</option>
          <option value="closed,duplicate,wont_fix,not_reproducible">Finished</option>
        </select>

        <select
          value={filters.severity[0] ?? ''}
          onChange={(event) => setFilter('severity', event.target.value || undefined)}
        >
          <option value="">Any severity</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>

        <button
          type="button"
          className="chip"
          aria-pressed={filters.slaBreached}
          onClick={() => setFilter('slaBreached', filters.slaBreached ? undefined : 'true')}
        >
          Past SLA only
        </button>

        <select value={filters.sort} onChange={(event) => setFilter('sort', event.target.value)}>
          <option value="created_desc">Newest first</option>
          <option value="created_asc">Oldest first</option>
          <option value="severity">Most severe first</option>
          <option value="sla_due">SLA deadline</option>
        </select>
      </div>

      {error && <div className="notice error">{error}</div>}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Ref</th>
              <th>System</th>
              <th className="title-cell">Report</th>
              <th>Severity</th>
              <th>Status</th>
              <th>Reporter</th>
              <th>SLA</th>
              <th>Raised</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const sla = slaState(row.slaDueAt, row.slaBreachedAt);
              return (
                <tr key={row.id}>
                  <td className="ref">{row.publicRef}</td>
                  <td className="nowrap">
                    {row.appDisplayName}
                    {row.component && <div className="muted">{row.component}</div>}
                  </td>
                  <td className="title-cell">
                    <Link to={`/item/${row.publicRef}`}>{row.title}</Link>
                    <div className="muted">
                      {typeLabel(row.type)}
                      {row.duplicateCount > 0 && ` · ${row.duplicateCount} also reported this`}
                      {row.attachmentCount > 0 && ' · screenshot'}
                    </div>
                  </td>
                  <td>
                    <span className={`badge sev-${row.severity}`}>{row.severity}</span>
                  </td>
                  <td className="nowrap">{statusLabel(row.status)}</td>
                  <td>
                    {row.reporter.displayName ?? '—'}
                    {row.reporter.department && (
                      <div className="muted">{row.reporter.department}</div>
                    )}
                  </td>
                  <td className={`nowrap${sla === 'breached' ? ' breached' : ''}`}>
                    {sla === 'none'
                      ? '—'
                      : sla === 'breached'
                        ? 'Past due'
                        : relativeTime(row.slaDueAt!)}
                  </td>
                  <td className="nowrap muted">{relativeTime(row.createdAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {!loading && rows.length === 0 && (
          <div className="empty">
            Nothing matches these filters.
            <br />
            {filters.slaBreached || filters.search
              ? 'Try widening the search.'
              : 'That is a good sign.'}
          </div>
        )}
        {loading && <div className="empty">Loading…</div>}
      </div>

      {cursor && (
        <p style={{ marginBlockStart: 14 }}>
          <button type="button" className="primary" onClick={() => void loadMore()}>
            Load more
          </button>
        </p>
      )}
    </>
  );
}
